// React
import React, { FC } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Reach
import { DialogOverlay, DialogContent } from '@reach/dialog';
import VisuallyHidden from '@reach/visually-hidden';
// Types
import { AvailableIcon } from '@utmb/types/map';
import { IconsEnum } from '@utmb/types/icons';
import { RaceTrackPointView } from '@utmb/types/index';
// Utils
import { getPointIcons } from '@utmb/utils/point';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
// Features
import { RaceTrackIcon } from '@utmb/features/race-track/RaceTrackIcon';
// Style
import styles from '@utmb/features/race-track/profile/race-profile-dialog.module.scss';
import '@reach/dialog/styles.css';
import PopupInfos from '@utmb/design-system/map/PopupInfos';
import { useRaceTrack } from '@utmb/contexts/RaceTrackContext';
import { usePreference } from '@utmb/contexts/PreferenceContext';
import { formatUnit } from '@utmb/utils/unit';
import { roundToDistanceInKmDecimal } from '@utmb/utils/distance';

export interface RaceProfileDialogProps {
    point: RaceTrackPointView;
    isOpen: boolean;
    close: () => void;
}

const RaceProfileDialog: FC<RaceProfileDialogProps> = ({ point, isOpen, close }) => {
    const { t } = useTranslation();

    const {
        track: { points },
    } = useRaceTrack();

    const previousPoint = points.reduce((prev, curr) => {
        if (curr.distance === point.distance) return prev;
        else if (curr.distance > point.distance) return prev;
        else return curr;
    });

    const {
        preferences: { preferredUnit },
    } = usePreference();

    const icons: AvailableIcon[] = getPointIcons(point);

    const badgeCSS = [styles.regular_badge, point.isAssistance && styles.dashed_badge].filter(Boolean).join(' ');

    return (
        <DialogOverlay className={styles.services_modal} isOpen={isOpen} onDismiss={close}>
            <VisuallyHidden>Close</VisuallyHidden>
            <DialogContent aria-label="Races services">
                <button className={styles.button_close} onClick={close}>
                    <Icon variant={IconsEnum.CLOSE} clss="color-secondary" />
                </button>
                {point?.uid && (
                    <div className={badgeCSS}>
                        <Font.FuturaHeavy mobile="16" desktop="18">
                            {point?.uid}
                        </Font.FuturaHeavy>
                    </div>
                )}

                <div className={styles.content_flex}>
                    <PopupInfos label={t(`raceTrack.popup.localisationLabel`)} content={point?.name} />
                    <PopupInfos label={t(`raceTrack.popup.elevationLabel`)} content={formatUnit(preferredUnit, 'elevation', point.elevation)} />
                    {point.distance != 0 && (
                        <>
                            <PopupInfos
                                label={t(`raceTrack.popup.distanceLabel`)}
                                content={formatUnit(preferredUnit, 'distance', roundToDistanceInKmDecimal(point.distance))}
                            />
                            <PopupInfos
                                label={t(`raceTrack.popup.distancePreviousLabel`, { name: previousPoint.shortName })}
                                content={formatUnit(preferredUnit, 'distance', roundToDistanceInKmDecimal(point.distanceFromLastPoint))}
                            />
                            <PopupInfos
                                label={t(`raceTrack.popup.elevationGainLabel`)}
                                content={formatUnit(preferredUnit, 'elevationGain', point.gainElevation)}
                            />
                            <PopupInfos
                                label={t(`raceTrack.popup.elevationGainPreviousLabel`, { name: previousPoint.shortName })}
                                content={formatUnit(preferredUnit, 'elevationGain', point.gainElevationFromLastPoint)}
                            />
                            <PopupInfos
                                label={t(`raceTrack.popup.elevationLossLabel`)}
                                content={formatUnit(preferredUnit, 'elevationLoss', point.lossElevation)}
                            />
                            <PopupInfos
                                label={t(`raceTrack.popup.elevationLossPreviousLabel`, { name: previousPoint.shortName })}
                                content={formatUnit(preferredUnit, 'elevationLoss', point.lossElevationFromLastPoint)}
                            />
                        </>
                    )}
                </div>
                <div className={styles.title_btn_flex}>
                    <Font.OxaniumLight mobile="14" desktop="16" className={styles.title}>
                        {t('raceTrack.popup.serviceLabel')}
                    </Font.OxaniumLight>
                </div>
                <div className={styles.service_wrapper}>
                    {icons.length === 0 && (
                        <Font.OxaniumBold className={styles.service_label} mobile="16" desktop="18" color="var(--utmb-color-race-primary-darken)">
                            {t(`raceTrack.popup.noService`)}
                        </Font.OxaniumBold>
                    )}
                    {icons.map((icon) => (
                        <div className={styles.service_content} key={icon}>
                            <RaceTrackIcon icon={icon} color="var(--utmb-color-race-primary-darken)" />
                            <Font.OxaniumBold className={styles.service_label} mobile="16" desktop="18" color="var(--utmb-color-race-primary-darken)">
                                {t(`raceTrack.service.${icon}`)}
                            </Font.OxaniumBold>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </DialogOverlay>
    );
};

export default RaceProfileDialog;
